import React, { useState, useEffect } from 'react';
import CustomerLayout from '../../components/layout/CustomerLayout';
import MapAddressPicker from '../../components/customer/MapAddressPicker';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { MapPin, Plus, Star, Trash2, Edit2, Home, Briefcase, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLoginToContinue } from '../../hooks/useLoginToContinue';
import { isSessionAuthError, takeLoginIntent } from '../../utils/loginIntent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = { Home, Work: Briefcase, Other: MapPin };

const CustomerAddresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const loginToContinue = useLoginToContinue();

  useEffect(() => {
    loadAddresses();
    // Back from logging in (the session had expired mid-edit): reopen the
    // address that was being added/edited, as it was typed.
    const intent = takeLoginIntent('/customer/addresses');
    if (intent?.action === 'add-address' || intent?.action === 'edit-address') {
      setEditingAddress(intent.draft || null);
      setShowMapPicker(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAddresses = async () => {
    try {
      const res = await axios.get(`${API}/customer/addresses`);
      setAddresses(res.data || []);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  };

  const handleSaveAddress = async (form) => {
    try {
      if (editingAddress?.id) {
        await axios.put(`${API}/customer/addresses/${editingAddress.id}`, form);
        toast.success('Address updated!');
      } else {
        await axios.post(`${API}/customer/addresses`, { ...form, is_default: addresses.length === 0 });
        toast.success('Address added!');
      }
      setShowMapPicker(false);
      setEditingAddress(null);
      loadAddresses();
    } catch (error) {
      if (isSessionAuthError(error)) {
        const editing = !!editingAddress?.id;
        setShowMapPicker(false);
        loginToContinue({
          message: editing ? 'Please log in to update this address.' : 'Please log in to add an address.',
          action: editing ? 'edit-address' : 'add-address',
          draft: editing ? { ...form, id: editingAddress.id } : form,
        });
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to save address');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/customer/addresses/${id}`);
      toast.success('Address removed');
      loadAddresses();
    } catch (error) {
      if (isSessionAuthError(error)) { loginToContinue({ message: 'Please log in to manage your addresses.' }); return; }
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      // The dedicated set-default endpoint only flips is_default — the general
      // update endpoint expects a full CustomerAddress body (full_address,
      // pincode, city are required) and would always reject a partial payload.
      await axios.put(`${API}/customer/addresses/${id}/set-default`);
      toast.success('Default address updated');
      loadAddresses();
    } catch (error) {
      if (isSessionAuthError(error)) { loginToContinue({ message: 'Please log in to manage your addresses.' }); return; }
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleEdit = (addr) => {
    setEditingAddress(addr);
    setShowMapPicker(true);
  };

  // Full-screen map picker
  if (showMapPicker) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white" data-testid="address-map-picker">
        <MapAddressPicker
          initialAddress={editingAddress}
          isEditing={!!editingAddress?.id}
          onSave={handleSaveAddress}
          onClose={() => { setShowMapPicker(false); setEditingAddress(null); }}
        />
      </div>
    );
  }

  return (
    <CustomerLayout title="My Addresses">
      <div className="p-4 space-y-3">
        {/* Add New Address Button */}
        <Button
          onClick={() => { setEditingAddress(null); setShowMapPicker(true); }}
          className="w-full h-12 rounded-xl border-2 border-dashed border-[var(--theme-primary)]/30 bg-[var(--theme-primary)]/5 text-[var(--theme-primary)] hover:bg-[var(--theme-primary)]/10"
          variant="ghost"
          data-testid="add-new-address-btn"
        >
          <Plus className="w-5 h-5 mr-2" /> Add New Address
        </Button>

        {/* Address List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--theme-primary)]" />
          </div>
        ) : addresses.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-16 h-16 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400">No addresses saved yet</p>
          </div>
        ) : (
          addresses.map(addr => {
            const TypeIcon = TYPE_ICONS[addr.label] || MapPin;
            return (
              <div key={addr.id} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" data-testid={`address-card-${addr.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${addr.is_default ? 'bg-[var(--theme-primary)]/10' : 'bg-gray-100'}`}>
                    <TypeIcon className={`w-5 h-5 ${addr.is_default ? 'text-[var(--theme-primary)]' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{addr.label}</span>
                      {addr.is_default && (
                        <span className="text-[10px] bg-[var(--theme-primary)]/10 text-[var(--theme-primary)] px-2 py-0.5 rounded-full font-medium">Default</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{addr.full_address}</p>
                    {addr.landmark && <p className="text-xs text-gray-400 mt-0.5">Near: {addr.landmark}</p>}
                    <p className="text-xs text-gray-400">{[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50">
                  {!addr.is_default && (
                    <button onClick={() => handleSetDefault(addr.id)} className="flex items-center gap-1 text-xs text-[var(--theme-primary)] font-medium" data-testid={`set-default-${addr.id}`}>
                      <Star className="w-3.5 h-3.5" /> Set Default
                    </button>
                  )}
                  <button onClick={() => handleEdit(addr)} className="flex items-center gap-1 text-xs text-gray-500 ml-auto" data-testid={`edit-addr-${addr.id}`}>
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDelete(addr.id)} className="flex items-center gap-1 text-xs text-red-500" data-testid={`delete-addr-${addr.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </CustomerLayout>
  );
};

export default CustomerAddresses;
